export const MESSAGING_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const

export const VOICE_RECORDER_FORMATS = [
  {
    mimeType: 'audio/webm;codecs=opus',
    uploadType: 'audio/webm',
    extension: 'webm',
  },
  {
    mimeType: 'audio/ogg;codecs=opus',
    uploadType: 'audio/ogg',
    extension: 'ogg',
  },
  {
    mimeType: 'audio/mp4;codecs=mp4a.40.2',
    uploadType: 'audio/mp4',
    extension: 'm4a',
  },
] as const

export const MAX_MESSAGE_PHOTOS = 4
export const MAX_VOICE_SECONDS = 120

export type MessagingMediaKind = 'photo' | 'voice' | 'document'
export type OutgoingMedia = { id: string; kind: MessagingMediaKind }
type MediaValidationReason =
  | 'photo_limit'
  | 'voice_limit'
  | 'voice_must_be_solo'

export type MediaValidationResult =
  | { ok: true }
  | { ok: false; reason: MediaValidationReason }

export type VoiceRecorderFormat = (typeof VOICE_RECORDER_FORMATS)[number]

type AttachmentContentType = { content_type: string }
type VerifiedAttachmentContentType = {
  content_type_verified?: string | null
}

function normalizeContentType(contentType: string): string {
  return contentType.split(';', 1)[0].trim().toLowerCase()
}

export function isMessagingPhotoType(contentType: string): boolean {
  const normalized = normalizeContentType(contentType)
  return MESSAGING_PHOTO_TYPES.some((candidate) => candidate === normalized)
}

export function isMessagingVoiceType(contentType: string): boolean {
  const normalized = normalizeContentType(contentType)
  return VOICE_RECORDER_FORMATS.some(
    ({ uploadType }) => uploadType === normalized,
  )
}

export function isVerifiedMessagingVoice(
  row: VerifiedAttachmentContentType,
): boolean {
  return (
    typeof row.content_type_verified === 'string' &&
    isMessagingVoiceType(row.content_type_verified)
  )
}

export function classifyAttachment(
  row: AttachmentContentType,
): MessagingMediaKind {
  if (isMessagingPhotoType(row.content_type)) return 'photo'
  if (isMessagingVoiceType(row.content_type)) return 'voice'
  return 'document'
}

export function validateOutgoingMedia(
  media: readonly OutgoingMedia[],
): MediaValidationResult {
  const photoCount = media.filter(({ kind }) => kind === 'photo').length
  const voiceCount = media.filter(({ kind }) => kind === 'voice').length

  if (photoCount > MAX_MESSAGE_PHOTOS) {
    return { ok: false, reason: 'photo_limit' }
  }
  if (voiceCount > 1) {
    return { ok: false, reason: 'voice_limit' }
  }
  if (voiceCount === 1 && media.length !== 1) {
    return { ok: false, reason: 'voice_must_be_solo' }
  }
  return { ok: true }
}

export function selectVoiceRecorderFormat(
  isSupported: (mimeType: string) => boolean,
): VoiceRecorderFormat | null {
  return (
    VOICE_RECORDER_FORMATS.find(({ mimeType }) => isSupported(mimeType)) ?? null
  )
}

export function canSendMessagingDraft(
  body: string,
  confirmedIds: readonly string[],
  busy: boolean,
): boolean {
  return !busy && (body.trim().length > 0 || confirmedIds.length > 0)
}
